<template>
  <div class="hb-login-wrapper">
    <div class="hb-login-layout">
      <!-- Left image section -->
      <section class="hb-hero">
        <div
          v-for="(img, index) in heroImages"
          :key="img"
          class="hb-hero-slide"
          :class="{ active: index === currentSlide }"
          :style="{ backgroundImage: `url(${img})` }"
        ></div>
        <div class="hb-hero-image-overlay"></div>
        <div class="hb-hero-caption">
          <p class="hb-hero-caption-title">Homa Bay County</p>
          <div class="hb-hero-dots">
            <span
              v-for="(img, index) in heroImages"
              :key="index"
              class="hb-dot"
              :class="{ active: index === currentSlide }"
              @click="goToSlide(index)"
            ></span>
          </div>
        </div>
      </section>

      <!-- Right auth panel (logo + branding + form) -->
      <section class="hb-auth-panel">
        <!-- Logo & branding at top -->
        <div class="hb-brand-header">
          <div class="hb-brand-logo">
            <img :src="homabayLogo" alt="Homa Bay County Logo" @error="homabayLogo = fallbackLogo" />
          </div>
          <div class="hb-brand-text">
            <h1>Homa Bay County</h1>
            <p class="hb-brand-subtitle">GOVERNMENT</p>
            <p class="hb-brand-description">
              Human Resources Information System (HRIS)<br />
              Streamlining workforce management across all county departments.
            </p>
          </div>
        </div>

        <!-- Divider -->
        <div class="hb-divider"></div>

        <!-- Auth form below -->
        <div class="hb-auth-header">
          <h2>Welcome back</h2>
          <p>Sign in to access the HR Information System</p>
        </div>

        <div class="hb-auth-form">
          <form @submit.prevent="login">
            <!-- Username Field -->
            <div class="form-group">
              <label class="form-label">Username</label>
              <div class="input-group">
                <i class="bx bx-user input-icon"></i>
                <input
                  v-model="username"
                  type="text"
                  class="form-control"
                  placeholder="Enter your username"
                  @keyup.enter="login"
                  autocomplete="username"
                  required
                >
              </div>
            </div>

            <!-- Password Field -->
            <div class="form-group">
              <label class="form-label">Password</label>
              <div class="input-group">
                <i class="bx bx-lock-alt input-icon"></i>
                <input
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-control"
                  placeholder="Enter your password"
                  @keyup.enter="login"
                  autocomplete="current-password"
                  required
                >
                <button
                  type="button"
                  class="password-toggle"
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                >
                  <i :class="showPassword ? 'bx bx-hide' : 'bx bx-show'"></i>
                </button>
              </div>
            </div>

            <!-- Remember Me & Forgot Password -->
            <div class="form-options">
              <label class="checkbox-container">
                <input
                  v-model="rememberMe"
                  type="checkbox"
                  class="checkbox-input"
                >
                <span class="checkbox-custom"></span>
                <span class="checkbox-label">Remember me</span>
              </label>
              <button type="button" class="forgot-link" @click="goToForgotPassword">
                Forgot password?
              </button>
            </div>

            <!-- Alert Messages -->
            <transition name="alert">
              <div v-if="alert.success" class="alert alert-success">
                <i class="bx bx-check-circle alert-icon"></i>
                {{ message }}
              </div>
            </transition>

            <transition name="alert">
              <div v-if="alert.info" class="alert alert-info">
                <i class="bx bx-info-circle alert-icon"></i>
                {{ message }}
              </div>
            </transition>

            <transition name="alert">
              <div v-if="alert.failed" class="alert alert-danger">
                <i class="bx bx-error-circle alert-icon"></i>
                {{ message }}
              </div>
            </transition>

            <!-- Login Button -->
            <button
              type="submit"
              class="btn-login"
              :disabled="isLoading"
            >
              <template v-if="!isLoading">
                Sign In
              </template>
              <template v-else>
                <span class="spinner"></span>
                Signing In...
              </template>
            </button>

          </form>
          <!-- Inline OTP input shown when server returns otpLength -->
          <div v-if="showOTP" class="hb-otp-inline" style="margin-top:12px">
            <label class="form-label">Enter OTP</label>
            <div class="input-group">
              <i class="bx bx-shield-alt input-icon"></i>
              <input
                v-model="otpValue"
                :maxlength="otpMaxLength"
                type="text"
                class="form-control"
                placeholder="Enter the OTP sent to your phone/email"
                @keyup.enter="verifyOTP"
              />
              <button type="button" class="btn-verify-otp" @click="verifyOTP" :disabled="isLoading">
                Verify
              </button>
              <button type="button" class="btn-cancel-otp" @click="cancelOTP" :disabled="isLoading">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Floating Extras (App Link, Helpline & Manual) to the left of LoginAssistant -->
    <div class="hb-floating-extras">
      <div class="hb-qr-code">
        <a href="https://api.craftcollect.africa/hrmis/app/HomabayHR.apk" target="_blank" title="Download Android App" style="display: flex; flex-direction: column; align-items: center; text-decoration: none; color: inherit;">
          <i class="bx bxl-android" style="font-size: 32px; color: #3ddc84; margin-bottom: 8px;"></i>
          <span style="text-align: center;">Download App</span>
        </a>
      </div>
      
      <div v-if="helplineNumber" class="hb-helpline">
        <i class="bx bx-support"></i>
        <div class="hb-helpline-text">
          <span>Helpline</span>
          <strong>{{ helplineNumber }}</strong>
        </div>
      </div>
      
      <div class="hb-qr-code">
        <a href="https://drive.google.com/file/d/16tY5UisIBkrI90HqiH8FuQSdWg0yrbSx/view?usp=sharing" target="_blank" title="Open Training Manual" style="display: flex; flex-direction: column; align-items: center; text-decoration: none; color: inherit;">
          <i class="bx bxs-book-reader" style="font-size: 32px; color: #007bff; margin-bottom: 8px;"></i>
          <span style="text-align: center;">Training Manual</span>
        </a>
      </div>
    </div>

    <LoginAssistant />

    <div v-if="showSessionConfirm" class="hb-session-modal-backdrop" @click.self="cancelSessionTakeover">
      <div class="hb-session-modal" role="dialog" aria-labelledby="session-modal-title">
        <h3 id="session-modal-title">Active session detected</h3>
        <p>{{ sessionConfirmMessage }}</p>
        <div class="hb-session-modal-actions">
          <button type="button" class="hb-session-btn hb-session-btn--ghost" @click="cancelSessionTakeover" :disabled="isLoading">
            Cancel
          </button>
          <button type="button" class="hb-session-btn hb-session-btn--primary" @click="confirmSessionTakeover" :disabled="isLoading">
            End other session &amp; continue
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, execute, execute2, readAndClearLogoutReason, toast } from "@/api";
import router from "@/router";
import fallbackLogo from "@/assets/logo.png";
import LoginAssistant from "@/components/LoginAssistant.vue";

export default {
  name: "LoginPage",
  components: { LoginAssistant },
  data() {
    const base = process.env.BASE_URL || '/';
    return {
      homabayLogo: base + "assets/homabay_logo.png",
      fallbackLogo,
      heroImages: [
        base + 'assets/bg-auth-overlay.webp',
        base + 'assets/mbita-bridge.webp',
      ],
      currentSlide: 0,
      slideInterval: null,
      username: '',
      password: '',
      rememberMe: false,
      showPassword: false,
      showOTP: false,
      otpValue: '',
      isLoading: false,
      message: '',
      qrCodeUrl: '',
      website: '',
      helplineNumber: '',
      alert: {
        success: false,
        info: false,
        failed: false
      },
      deviceDetails: '',
      currentYear: new Date().getFullYear(),
      showSessionConfirm: false,
      sessionConfirmMessage: 'You are already signed in on another device. End that session to continue here?'
    }
  },
  mounted() {
    this.getDeviceDetails();
    this.fetchPublicCompanyInfo();
    // Show forced-logout reason (e.g. user logged in on another device)
    const forcedMsg = readAndClearLogoutReason();
    if (forcedMsg) {
      this.message = forcedMsg;
      this.alert.info = true;
      try { toast.info(forcedMsg); } catch (_) { /* ignore toast failures */ }
    }
    this.loadRememberedCredentials();
    this.startSlideshow();
  },
  beforeUnmount() {
    clearInterval(this.slideInterval);
  },
  methods: {
    async fetchPublicCompanyInfo() {
      try {
        const data = new FormData();
        data.append("function", "getPublicCompanyInfo");
        const res = await execute(data, authUrl);
        if (res?.data?.success && res.data.data?.companyInfo) {
          const info = res.data.data.companyInfo;
          this.qrCodeUrl = info.qrCodeUrl || "";
          this.helplineNumber = info.helplineNumber || "";
          this.website = info.website || "";
        }
      } catch (err) {
        console.error("Failed to load public company info", err);
      }
    },
    startSlideshow() {
      this.slideInterval = setInterval(() => {
        this.currentSlide = (this.currentSlide + 1) % this.heroImages.length;
      }, 5000);
    },
    goToSlide(index) {
      this.currentSlide = index;
      clearInterval(this.slideInterval);
      this.startSlideshow();
    },
    goToForgotPassword() {
      this.$router.push('/forgot-password');
    },

    loginErrorMessage(resOrResponse, err) {
      const data = resOrResponse?.data ?? resOrResponse;
      const msg = String(data?.message || data?.Message || "").trim();
      if (msg && !/^error\s*500$/i.test(msg) && !/^internal server error$/i.test(msg)) {
        return msg;
      }
      const status = resOrResponse?.status ?? err?.response?.status;
      if (status >= 500 || status === 0 || !data) {
        return "Wrong username or password.";
      }
      return msg || "Wrong username or password.";
    },

    getDeviceDetails() {
      let os = 'Unknown';
      if (navigator.userAgent.indexOf('Win') !== -1) os = 'Windows';
      else if (navigator.userAgent.indexOf('Mac') !== -1) os = 'MacOS';
      else if (navigator.userAgent.indexOf('Linux') !== -1) os = 'Linux';
      else if (navigator.userAgent.indexOf('Android') !== -1) os = 'Android';
      else if (navigator.userAgent.indexOf('iOS') !== -1) os = 'iOS';

      let browser = 'Unknown';
      if (navigator.userAgent.indexOf('Chrome') !== -1) browser = 'Chrome';
      else if (navigator.userAgent.indexOf('Firefox') !== -1) browser = 'Firefox';
      else if (navigator.userAgent.indexOf('Safari') !== -1) browser = 'Safari';
      else if (navigator.userAgent.indexOf('Edge') !== -1) browser = 'Edge';
      else if (navigator.userAgent.indexOf('MSIE') !== -1 || !!document.documentMode) browser = 'Internet Explorer';

      const screenResolution = `${window.screen.width} x ${window.screen.height}`;
      const cpuCores = navigator.hardwareConcurrency || 'Unknown';
      const memory = navigator.deviceMemory || 'Unknown';

      this.deviceDetails = `os: ${os} | browser: ${browser} | screenResolution: ${screenResolution} | cpuCores: ${cpuCores} | memory: ${memory}`;
    },

    loadRememberedCredentials() {
      try {
        const rememberedUsername = localStorage.getItem('rememberedUsername');
        const rememberedPassword = localStorage.getItem('rememberedPassword');

        if (rememberedUsername) {
          this.username = rememberedUsername;
          this.rememberMe = true;

          if (rememberedPassword) {
            try {
              this.password = atob(rememberedPassword);
            } catch (e) {
              console.error('Error decoding saved password');
              localStorage.removeItem('rememberedPassword');
            }
          }
        }
      } catch (error) {
        console.error('Error loading remembered credentials:', error);
      }
    },

    saveRememberedCredentials() {
      try {
        if (this.rememberMe) {
          localStorage.setItem('rememberedUsername', this.username);
          localStorage.setItem('rememberedPassword', btoa(this.password));
        } else {
          localStorage.removeItem('rememberedUsername');
          localStorage.removeItem('rememberedPassword');
        }
      } catch (error) {
        console.error('Error saving remembered credentials:', error);
      }
    },

    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    async login(confirmEndOtherSession = false) {
      if (!this.username || !this.password) {
        this.clearAlerts();
        this.message = "Please enter both username and password";
        this.alert.failed = true;
        return;
      }

      this.clearAlerts();
      if (!confirmEndOtherSession) {
        this.message = "Authorizing...";
        this.alert.info = true;
      }
      this.isLoading = true;

      const data = new FormData();
      data.append("function", "loginPortal");
      data.append("email", this.username);
      data.append("password", btoa(this.password.trim()));
      data.append("version", "1");
      data.append("deviceDetails", this.deviceDetails);
      if (confirmEndOtherSession) {
        data.append("confirmEndOtherSession", "1");
      }

      try {
        const res = await execute2(data, authUrl);

        this.clearAlerts();
        this.isLoading = false;

        if (res.data.data?.requiresSessionConfirm) {
          this.sessionConfirmMessage = res.data.message || this.sessionConfirmMessage;
          this.showSessionConfirm = true;
          return;
        }

        if (res.data.success) {
          this.saveRememberedCredentials();

          const tokenSession = res.data.data.tokenSession;
          const token = tokenSession?.token || tokenSession;
          if (token) {
            sessionStorage.setItem("tokenSession", token);
          }

          const roles = res.data.data.roles;
          if (roles) {
            sessionStorage.setItem("selectedRoles", JSON.stringify(roles));
          }

          const passwordExpiringSoon = !!res.data.data.passwordExpiringSoon;
          const passwordDaysRemaining = res.data.data.passwordDaysRemaining;
          if (passwordExpiringSoon && passwordDaysRemaining != null) {
            sessionStorage.setItem("passwordExpiringSoon", "true");
            sessionStorage.setItem("passwordDaysRemaining", String(passwordDaysRemaining));
          } else {
            sessionStorage.removeItem("passwordExpiringSoon");
            sessionStorage.removeItem("passwordDaysRemaining");
          }

          if (!res.data?.data?.user) {
            if (res.data.data.otpLength) {
              sessionStorage.setItem("otpLength", res.data.data.otpLength);
            }
            this.showOTP = true;
            this.otpValue = '';
            this.message = res.data.data.message || 'OTP sent. Enter it below.';
            this.alert.info = true;
            if (passwordExpiringSoon) {
              const days = passwordDaysRemaining === 0
                ? 'today'
                : passwordDaysRemaining === 1
                  ? 'tomorrow'
                  : `in ${passwordDaysRemaining} days`;
              this.message = `OTP sent. Your password expires ${days}. Please change it after signing in.`;
              this.alert.info = true;
            }
          } else {
            const userData = res.data.data.user;

            sessionStorage.setItem("isLoggedIn", "true");
            sessionStorage.setItem("userInfo", JSON.stringify({
              names: userData.names,
              idNo: userData.idNo,
              phoneNumber: userData.phoneNumber,
              category: userData.category,
              categoryId: userData.categoryId,
              requireOTP: userData.requireOTP,
              passwordChanged: userData.passwordChanged,
              passwordExpiry: userData.passwordExpiry,
              passwordExpiringSoon,
              passwordDaysRemaining
            }));

            if (passwordExpiringSoon) {
              const days = passwordDaysRemaining === 0
                ? 'today'
                : passwordDaysRemaining === 1
                  ? 'tomorrow'
                  : `in ${passwordDaysRemaining} days`;
              this.message = `Login successful. Your password expires ${days}. Please change it soon (check your email/SMS for a reminder).`;
            } else {
              this.message = res.data.message || "Login successful!";
            }
            this.alert.success = true;

            try {
              const { authUrl, execute, fetchPermissionsAndMenus } = await import("@/api");
              await fetchPermissionsAndMenus(authUrl, execute);
            } catch (_) {
              /* ignore if RBAC fetch fails */
            }

            if (userData.passwordChanged === 'false' || userData.passwordChanged === false || userData.passwordChanged === '0') {
              setTimeout(() => {
                router.push('change-password');
              }, 500);
              return;
            }

            if (userData.requireOTP === 'true') {
              if (res.data.data.otpLength) {
                sessionStorage.setItem("otpLength", res.data.data.otpLength);
              }
              router.push('otp');
            } else {
              setTimeout(() => {
                if (userData.category === 'EMPLOYEES') {
                  router.push('employee-dashboard');
                } else {
                  router.push('dashboard');
                }
              }, 500);
            }
          }
        } else {
          sessionStorage.setItem("isLoggedIn", "false");
          this.alert.failed = true;
          this.message = this.loginErrorMessage(res);
        }
      } catch (error) {
        this.clearAlerts();
        this.isLoading = false;
        this.alert.failed = true;
        this.message = this.loginErrorMessage(error?.response, error);
        console.error("Login error:", error);
      }
    },

    confirmSessionTakeover() {
      this.showSessionConfirm = false;
      this.login(true);
    },

    cancelSessionTakeover() {
      this.showSessionConfirm = false;
      this.clearAlerts();
      this.message = "Sign-in cancelled. Your other session is still active.";
      this.alert.info = true;
    }
,

    async verifyOTP() {
      if (!this.otpValue || this.otpValue.trim() === '') {
        this.message = 'Please enter the OTP';
        this.alert.failed = true;
        return;
      }
      this.isLoading = true;
      this.clearAlerts();
      try {
        const data = new FormData();
        data.append('function', 'OTPVerification');
        data.append('enteredOTP', this.otpValue.trim());
        const resp = await execute(data, authUrl);
        this.isLoading = false;
        if (resp.data && resp.data.success) {
          // Successful verification returns user data — set session and continue
          const user = resp.data.data.user;
          if (user) {
            sessionStorage.setItem('isLoggedIn', 'true');
            sessionStorage.setItem('userInfo', JSON.stringify(user));
          }
          router.push('dashboard');
        } else {
          this.alert.failed = true;
          this.message = resp.data?.message || 'Invalid OTP';
        }
      } catch (e) {
        this.isLoading = false;
        this.alert.failed = true;
        this.message = e?.response?.data?.message || 'OTP verification failed';
      }
    },

    cancelOTP() {
      this.showOTP = false;
      this.otpValue = '';
      sessionStorage.removeItem('otpLength');
    },
  },
  computed: {
    otpMaxLength() {
      try {
        if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return 6;
        const v = sessionStorage.getItem('otpLength');
        const n = parseInt(String(v || '6'), 10);
        return Number.isNaN(n) ? 6 : n;
      } catch (e) {
        return 6;
      }
    }
  }
}
</script>

<style scoped>
.hb-login-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  background: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
}

.hb-login-layout {
  width: 100%;
  max-width: none;
  margin: 0;
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  background: #0e3a1b;
  border-radius: 0;
  overflow: hidden;
  box-shadow: none;
  color: #ffffff;
}

/* ── Left image panel ── */
.hb-hero {
  position: relative;
  overflow: hidden;
}

.hb-hero-slide {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  opacity: 0;
  transition: opacity 1s ease-in-out;
}

.hb-hero-slide.active {
  opacity: 1;
}

.hb-hero-image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.15) 0%,
    rgba(0, 0, 0, 0.05) 50%,
    rgba(0, 0, 0, 0.60) 100%
  );
  z-index: 1;
}

.hb-hero-caption {
  position: absolute;
  bottom: 28px;
  left: 28px;
  z-index: 2;
}

.hb-hero-caption-title {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.04em;
  text-shadow: 0 1px 4px rgba(0,0,0,0.4);
}

.hb-hero-caption-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.hb-hero-dots {
  display: flex;
  gap: 6px;
  margin-top: 10px;
}

.hb-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.45);
  cursor: pointer;
  transition: background 0.3s ease, transform 0.3s ease;
}

.hb-dot.active {
  background: #ffffff;
  transform: scale(1.3);
}

/* ── Right panel ── */
.hb-auth-panel {
  background: #f9fafb;
  color: #020617;
  padding: 32px 40px 28px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: auto;
}

/* Brand block */
.hb-brand-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.hb-brand-logo img {
  width: 72px;
  height: 72px;
  object-fit: contain;
  border-radius: 12px;
  flex-shrink: 0;
}

.hb-brand-text h1 {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}

.hb-brand-subtitle {
  font-size: 10px;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: #2195d3;
  font-weight: 600;
  margin-top: 2px;
}

.hb-brand-description {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.6;
  margin-top: 6px;
}

.hb-divider {
  height: 1px;
  background: #e5e7eb;
  margin-bottom: 24px;
}

/* Auth form section */
.hb-auth-header {
  margin-bottom: 20px;
}

.hb-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 600;
}

.hb-auth-header h2 {
  margin-top: 12px;
  font-size: 24px;
  font-weight: 700;
  color: #111827;
}

.hb-auth-header p {
  margin-top: 4px;
  font-size: 13px;
  color: #6b7280;
}

.hb-auth-form {
  flex: 1;
}

.form-group {
  margin-bottom: 18px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
}

.input-group {
  position: relative;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  display: flex;
  align-items: center;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.input-icon {
  padding-left: 14px;
  padding-right: 4px;
  font-size: 18px;
  color: #6b7280;
}

.form-control {
  flex: 1;
  border: none;
  padding: 12px 14px;
  font-size: 14px;
  color: #111827;
  background: transparent;
}

.form-control::placeholder {
  color: #9ca3af;
}

.form-control:focus {
  outline: none;
}

.password-toggle {
  border: none;
  background: transparent;
  padding: 0 12px 0 4px;
  color: #6b7280;
  cursor: pointer;
  font-size: 18px;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  font-size: 13px;
}

.checkbox-container {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-input {
  position: absolute;
  opacity: 0;
}

.checkbox-custom {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  position: relative;
}

.checkbox-input:checked + .checkbox-custom {
  background: #2195d3;
  border-color: #2195d3;
}

.checkbox-input:checked + .checkbox-custom::after {
  content: "✓";
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 11px;
}

.checkbox-label {
  color: #2195d3;
}

.forgot-link {
  border: none;
  background: none;
  padding: 0;
  font-size: 13px;
  color: #2195d3;
  font-weight: 600;
  cursor: pointer;
}

.forgot-link:hover {
  text-decoration: underline;
}

.alert {
  padding: 10px 12px;
  border-radius: 10px;
  margin-bottom: 14px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.alert-success {
  background: #dcfce7;
  color: #2195d3;
}

.alert-info {
  background: #e0f2fe;
  border: 1px solid #3b82f655;
  color: #1d4ed8;
}

.alert-danger {
  background: #fee2e2;
  border: 1px solid #ef444455;
  color: #b91c1c;
}

.alert-icon {
  font-size: 18px;
}

.btn-login {
  width: 100%;
  margin-top: 8px;
  padding: 12px 16px;
  border-radius: 999px;
  border: none;
  background: #2195d3;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease;
}

.btn-login:hover:enabled {
  transform: translateY(-1px);
}

.btn-login:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  box-shadow: none;
}

.spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Transitions for alerts */
.alert-enter-active,
.alert-leave-active {
  transition: all 0.2s ease;
}

.alert-enter-from,
.alert-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Responsive */
@media (max-width: 960px) {
  .hb-login-layout {
    grid-template-columns: 1fr;
  }

  .hb-hero {
    height: 220px;
  }
}

.hb-session-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 16px;
}

.hb-session-modal {
  background: #fff;
  border-radius: 12px;
  max-width: 420px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.18);
}

.hb-session-modal h3 {
  margin: 0 0 12px;
  font-size: 1.15rem;
  color: #0f172a;
}

.hb-session-modal p {
  margin: 0 0 20px;
  color: #475569;
  line-height: 1.5;
}

.hb-session-modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.hb-session-btn {
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
}

.hb-session-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.hb-session-btn--ghost {
  background: #f1f5f9;
  color: #334155;
}

.hb-session-btn--primary {
  background: #2563eb;
  color: #fff;
}

@media (max-width: 640px) {
  .hb-auth-panel {
    padding: 24px 20px 20px;
  }

  .form-options {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

.hb-floating-extras {
  position: fixed;
  bottom: 24px;
  right: 180px;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 9998;
}

@media (max-width: 768px) {
  .hb-floating-extras {
    bottom: 80px; /* Above the assistant button if it stacks */
    right: 24px;
  }
}
.hb-qr-code {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  padding: 4px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
.hb-qr-code img {
  width: 50px;
  height: 50px;
  object-fit: contain;
}
.hb-qr-code span {
  font-size: 0.65rem;
  font-weight: 600;
  color: #1e293b;
  margin-top: 2px;
}
.hb-helpline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(15, 23, 42, 0.05);
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
}
.hb-helpline i {
  font-size: 1.5rem;
  color: #3b82f6;
}
.hb-helpline-text {
  display: flex;
  flex-direction: column;
}
.hb-helpline-text span {
  font-size: 0.7rem;
  color: #64748b;
  text-transform: uppercase;
  font-weight: 600;
}
.hb-helpline-text strong {
  font-size: 0.95rem;
  color: #0f172a;
}
</style>